import { Action, Reducer } from 'redux';
import { ApplicationState, AppThunkAction, AppThunkActionAsync } from '../../../';
import { DataResult, ToxnotServiceClient } from '../../../ToxnotServiceClient';
import {
    AiSurveyOperation,
    DataCollectionAiAgentOperationCode,
    DataCollectionAiAgentOperationState,
    DataCollectionAiAgentOperationType,
    CoreSurveyResponseData,
    SupplierResponseAlreadyProvidedResult
} from './SurveyResponseModels';
import { SupplierAiWorkflowRequest, AiDeleteAllAnswersStartRequest, AiTerminateRequest } from '../../../Models/Surveys/SurveyDataModel';
import { store } from '../../../..';

// ===============================================================================
// Types & Interfaces
// ===============================================================================

/**
 * State interface for AI Survey Agent operations
 */
export interface AISurveyAgentState {
    /** Current AI operation data, null if no operation is active */
    currentOperation: AiSurveyOperation | null;

    /** Loading state for AI operations */
    isLoading: boolean;

    /** Error message if operation failed */
    errorMessage: string | null;

    /** Whether polling is currently active */
    isPolling: boolean;

    /** Timestamp of the last successful poll */
    lastPolledAt: string | null;
}

/**
 * Redux action types
 */
const AISurveyAgentSetState = "SET_AI_SURVEY_AGENT_STATE";
interface AISurveyAgentSetState {
    type: typeof AISurveyAgentSetState;
    value: Partial<AISurveyAgentState>;
}

type KnownAction = AISurveyAgentSetState;

// ===============================================================================
// Constants
// ===============================================================================

const POLLING_INTERVAL_MS = 5000; // Poll every 5 seconds
const MAX_POLLING_RETRIES = 10; // Maximum consecutive failures before stopping

// ===============================================================================
// Private State
// ===============================================================================

let aiOperationPollingInterval: NodeJS.Timeout | null = null;
let pollingRetryCount = 0;

const serviceClient = new ToxnotServiceClient();

// ===============================================================================
// Initial State
// ===============================================================================

function getInitialState(): AISurveyAgentState {
    return {
        currentOperation: null,
        isLoading: false,
        errorMessage: null,
        isPolling: false,
        lastPolledAt: null
    };
}

// ===============================================================================
// Helper Functions
// ===============================================================================

/**
 * Checks if the AI operation is in a readonly state (processing)
 * @param operation The AI operation to check
 * @returns True if the operation is in a readonly state
 */
export const isAIOperationReadonly = (operation: AiSurveyOperation | null): boolean => {
    if (!operation || operation.state === null || operation.state === undefined) {
        return false;
    }

    const readonlyStates = [
        DataCollectionAiAgentOperationState.Triggered,
        DataCollectionAiAgentOperationState.RequestInProgress,
        DataCollectionAiAgentOperationState.ResponseReceived,
        DataCollectionAiAgentOperationState.PreparingGapSummary,
        DataCollectionAiAgentOperationState.DeleteInProgress
    ];

    const stateValue = operation.state as unknown;
    const enumValue = typeof stateValue === 'string'
        ? DataCollectionAiAgentOperationState[stateValue as keyof typeof DataCollectionAiAgentOperationState]
        : stateValue;

    return typeof enumValue === 'number' && readonlyStates.includes(enumValue);
};

/**
 * Checks if the AI operation completed successfully
 * @param operation The AI operation to check
 * @returns True if the operation completed successfully
 */
export const isAIOperationSuccess = (operation: AiSurveyOperation | null): boolean => {
    if (!operation || operation.state === null || operation.state === undefined) {
        return false;
    }

    const stateValue = operation.state as unknown;
    const enumValue = typeof stateValue === 'string'
        ? DataCollectionAiAgentOperationState[stateValue as keyof typeof DataCollectionAiAgentOperationState]
        : stateValue;

    return enumValue === DataCollectionAiAgentOperationState.ResponseProcessed;
};

/**
 * Checks if a delete-all operation has completed.
 * @param operation The AI operation to check
 * @returns True if delete-all completed successfully
 */
export const isAIOperationDeleteCompleted = (operation: AiSurveyOperation | null): boolean => {
    if (!operation || operation.state === null || operation.state === undefined) {
        return false;
    }

    const stateValue = operation.state as unknown;
    const enumValue = typeof stateValue === 'string'
        ? DataCollectionAiAgentOperationState[stateValue as keyof typeof DataCollectionAiAgentOperationState]
        : stateValue;

    return enumValue === DataCollectionAiAgentOperationState.DeleteCompleted;
};

/**
 * Checks if a delete-all operation is currently in progress.
 * @param operation The AI operation to check
 * @returns True if delete-all is currently in progress
 */
export const isAIOperationDeleteInProgress = (operation: AiSurveyOperation | null): boolean => {
    if (!operation || operation.state === null || operation.state === undefined) {
        return false;
    }

    const enumValue = parseAiOperationStateValue(operation.state);

    return enumValue === DataCollectionAiAgentOperationState.DeleteInProgress;
};

/**
 * Checks if the AI operation failed
 * @param operation The AI operation to check
 * @returns True if the operation failed
 */
export const isAIOperationFailed = (operation: AiSurveyOperation | null): boolean => {
    if (!operation || operation.state === null || operation.state === undefined) {
        return false;
    }

    const enumValue = parseAiOperationStateValue(operation.state);

    return enumValue === DataCollectionAiAgentOperationState.Failed
        || isTerminatedAiOperationState(operation.state);
};

export const parseAiOperationStateValue = (
    stateValue: unknown
): DataCollectionAiAgentOperationState | null => {
    if (stateValue === null || stateValue === undefined) {
        return null;
    }

    if (typeof stateValue === 'number') {
        return stateValue;
    }

    const enumValue = DataCollectionAiAgentOperationState[stateValue as keyof typeof DataCollectionAiAgentOperationState];
    return typeof enumValue === 'number' ? enumValue : null;
};

export const parseAiOperationTypeValue = (
    operationType: unknown
): DataCollectionAiAgentOperationType | null => {
    if (operationType === null || operationType === undefined) {
        return null;
    }

    if (typeof operationType === 'number') {
        return operationType;
    }

    const enumValue = DataCollectionAiAgentOperationType[operationType as keyof typeof DataCollectionAiAgentOperationType];
    return typeof enumValue === 'number' ? enumValue : null;
};

export const parseAiOperationCodeValue = (
    operationCode: unknown
): DataCollectionAiAgentOperationCode | null => {
    if (operationCode === null || operationCode === undefined) {
        return null;
    }

    if (typeof operationCode === 'number') {
        return operationCode;
    }

    const enumValue = DataCollectionAiAgentOperationCode[operationCode as keyof typeof DataCollectionAiAgentOperationCode];
    return typeof enumValue === 'number' ? enumValue : null;
};

export const isTerminatedAiOperationState = (stateValue: unknown): boolean => {
    const enumValue = parseAiOperationStateValue(stateValue);

    return enumValue === DataCollectionAiAgentOperationState.TerminatedByExchange
        || enumValue === DataCollectionAiAgentOperationState.TerminatedByUser;
};

/**
 * Checks if the AI operation is in a terminal state (completed or failed)
 * @param operation The AI operation to check
 * @returns True if the operation has finished processing
 */
export const isAIOperationFinished = (operation: AiSurveyOperation | null): boolean => {
    return isAIOperationSuccess(operation) || isAIOperationDeleteCompleted(operation) || isAIOperationFailed(operation);
};

/**
 * Whether the offline "Show Gap Analysis" CTA/flow applies for the given operation:
 * an offline response whose active operation is not set to auto-accept. Shared so the
 * button label (AISuccessState) and the navigation gate (SupplierAiAgent) cannot drift.
 */
export const shouldShowOfflineGapAnalysisCta = (
    operationType: DataCollectionAiAgentOperationType | null | undefined,
    aiOperation: AiSurveyOperation | null | undefined
): boolean =>
    operationType === DataCollectionAiAgentOperationType.OfflineResponse
    && aiOperation?.metadata?.autoAcceptOfflineResponse !== true;

export const getActiveAIOperationFromState = (
    aiSurveyAgentState?: AISurveyAgentState | null,
    operationType?: DataCollectionAiAgentOperationType | null
): AiSurveyOperation | null => {
    const currentOperation = aiSurveyAgentState?.currentOperation ?? null;

    if (!currentOperation || operationType === null || operationType === undefined) {
        return currentOperation;
    }

    return parseAiOperationTypeValue(currentOperation.type) === operationType
        ? currentOperation
        : null;
};

/**
 * Gets the current AI operation from Redux state
 * @returns The current AI operation or null
 */
export const getCurrentAIOperation = (
    operationType?: DataCollectionAiAgentOperationType | null
): AiSurveyOperation | null => {
    return getActiveAIOperationFromState(store.getState().aiSurveyAgentState, operationType);
};

/**
 * Checks if AI operation polling is currently active
 * @returns True if polling is active
 */
export const isAIOperationPollingActive = (): boolean => {
    return aiOperationPollingInterval !== null;
};

// ===============================================================================
// Action Creators
// ===============================================================================

/**
 * Retriggers a failed AI workflow operation by creating a new operation with the same parameters
 * @param requestId The survey request ID
 * @param operationId The failed operation ID to retry
 * @returns Promise with the new operation ID
 */
export const retriggerAIWorkflow = (
    requestId: string,
    operationId: string
) =>
    async (dispatch: (action: KnownAction) => void, getState: () => ApplicationState): Promise<DataResult<string>> => {
        try {
            // Validate inputs
            if (!requestId || requestId.trim() === '') {
                return {
                    success: false,
                    result: 'Error',
                    data: null
                };
            }

            if (!operationId || operationId.trim() === '') {
                return {
                    success: false,
                    result: 'Error',
                    data: null
                };
            }

            // Set loading state
            dispatch({
                type: AISurveyAgentSetState,
                value: {
                    isLoading: true,
                    errorMessage: null
                }
            });

            // Call API to retrigger the workflow
            const result = await serviceClient.retriggerSupplierAiWorkflow(requestId, operationId);

            if (result.success && result.data) {
                // Get the existing operation to retrieve its configuration
                const state = getState();
                const existingOperation = state.aiSurveyAgentState?.currentOperation;

                // Create initial AI operation in Triggered state with new operation ID
                const initialOperation: AiSurveyOperation = {
                    operationId: result.data,
                    requestId: requestId,
                    ownerId: existingOperation?.ownerId || null,
                    userWhoInitiatedTheRequest: existingOperation?.userWhoInitiatedTheRequest || null,
                    type: existingOperation?.type || null,
                    state: DataCollectionAiAgentOperationState.Triggered,
                    agentOperationCode: null,
                    agentState: null,
                    fileIds: existingOperation?.fileIds || null,
                    isDataOverriden: existingOperation?.isDataOverriden || false,
                    dateInitiated: new Date().toISOString(),
                    dateModified: null,
                    dateResponseReceived: null,
                    dateCompleted: null,
                    responseOperationId: null,
                    gapSummary: null,
                    queueTasksAhead: null
                };

                // Update Redux state with the new operation
                dispatch({
                    type: AISurveyAgentSetState,
                    value: {
                        currentOperation: initialOperation,
                        isLoading: true,
                        errorMessage: null
                    }
                });

                // Start polling for status updates
                startAIOperationPolling(requestId, result.data);

                return {
                    success: true,
                    result: 'Ok',
                    data: result.data
                };
            } else {
                // Handle API error
                dispatch({
                    type: AISurveyAgentSetState,
                    value: {
                        isLoading: false,
                        errorMessage: result.result === SupplierResponseAlreadyProvidedResult
                            ? null
                            : 'Failed to retrigger AI workflow'
                    }
                });

                return result;
            }
        } catch (error) {
            // Handle unexpected errors
            dispatch({
                type: AISurveyAgentSetState,
                value: {
                    isLoading: false,
                    errorMessage: 'An unexpected error occurred'
                }
            });

            return {
                success: false,
                result: 'Error',
                data: null
            };
        }
    };

// ===============================================================================

/**
 * Triggers a new AI workflow for processing survey documents
 * @param requestId The survey request ID
 * @param fileIds Array of file IDs to process
 * @param overrideExistingData Whether to override existing survey data
 * @param operationType The type of AI operation (Survey or OfflineResponse)
 * @param autoAcceptSurveyResponse Whether to auto-accept an offline AI response after processing
 * @param enableWebRetrieval Whether the agent may supplement uploaded documents with public web sources
 * @param webRetrievalUrls Valid public source URLs supplied by the user
 * @param userNotes Optional contextual instructions supplied with the uploaded documents
 * @returns Promise with the operation result
 */
export const triggerAIWorkflow = (
    requestId: string,
    fileIds: string[],
    overrideExistingData: boolean,
    operationType: DataCollectionAiAgentOperationType = DataCollectionAiAgentOperationType.Survey,
    autoAcceptSurveyResponse: boolean = false,
    enableWebRetrieval: boolean = false,
    webRetrievalUrls: string[] = [],
    userNotes: string = ''
) =>
    async (dispatch: (action: KnownAction) => void, getState: () => ApplicationState): Promise<DataResult<string>> => {
        try {
            // Validate inputs
            if (!requestId || requestId.trim() === '') {
                return {
                    success: false,
                    result: 'Error',
                    data: null
                };
            }

            if (!fileIds || fileIds.length === 0) {
                return {
                    success: false,
                    result: 'Error',
                    data: null
                };
            }

            // Set loading state
            dispatch({
                type: AISurveyAgentSetState,
                value: {
                    isLoading: true,
                    errorMessage: null
                }
            });

            // Prepare request
            const request: SupplierAiWorkflowRequest = {
                requestId,
                fileIds,
                isDataOverriden: overrideExistingData,
                operationType,
                autoAcceptSurveyResponse,
                enableWebRetrieval,
                webRetrievalUrls,
                userNotes
            };

            // Call API
            const result = await serviceClient.triggerSupplierAiWorkflow(request);

            if (result.success && result.data) {
                // Create initial AI operation in Triggered state
                const initialOperation: AiSurveyOperation = {
                    operationId: result.data,
                    requestId: requestId,
                    ownerId: null,
                    userWhoInitiatedTheRequest: null,
                    type: operationType,
                    state: DataCollectionAiAgentOperationState.Triggered,
                    agentOperationCode: null,
                    agentState: null,
                    fileIds: fileIds,
                    isDataOverriden: overrideExistingData,
                    dateInitiated: new Date().toISOString(),
                    dateModified: null,
                    dateResponseReceived: null,
                    dateCompleted: null,
                    responseOperationId: null,
                    metadata: {
                        autoAcceptOfflineResponse: operationType === DataCollectionAiAgentOperationType.OfflineResponse
                            && autoAcceptSurveyResponse
                    },
                    webRetrievalConfig: {
                        enabled: enableWebRetrieval,
                        urls: webRetrievalUrls,
                        userNotes
                    },
                    gapSummary: null,
                    queueTasksAhead: null
                };

                // Update local state
                dispatch({
                    type: AISurveyAgentSetState,
                    value: {
                        currentOperation: initialOperation,
                        isLoading: true,
                        errorMessage: null
                    }
                });

                // Start polling for status updates
                startAIOperationPolling(requestId, result.data);
            } else {
                dispatch({
                    type: AISurveyAgentSetState,
                    value: {
                        isLoading: false,
                        errorMessage: result.result === SupplierResponseAlreadyProvidedResult
                            ? null
                            : 'Failed to trigger AI workflow'
                    }
                });
            }

            return result;

        } catch (error) {

            dispatch({
                type: AISurveyAgentSetState,
                value: {
                    isLoading: false,
                    errorMessage: 'Network error occurred while triggering AI workflow'
                }
            });

            return {
                success: false,
                result: 'Error',
                data: null
            };
        }
    };

/**
 * Starts the delete-all AI answers workflow.
 * @param requestId The survey request ID
 * @param operationId The source AI operation ID
 * @returns Promise with the newly created delete operation ID
 */
export const startDeleteAllAiAnswersFlow = (
    requestId: string,
    operationId: string
) =>
    async (dispatch: (action: KnownAction) => void, getState: () => ApplicationState): Promise<DataResult<string>> => {
        try {
            if (!requestId || requestId.trim() === '') {
                return {
                    success: false,
                    result: 'Error',
                    data: null
                };
            }

            if (!operationId || operationId.trim() === '') {
                return {
                    success: false,
                    result: 'Error',
                    data: null
                };
            }

            const existingOperation = getState().aiSurveyAgentState?.currentOperation;
            const deleteInProgressOperation: AiSurveyOperation = {
                ...(existingOperation ?? {
                    ownerId: null,
                    requestId: requestId,
                    operationId: operationId,
                    userWhoInitiatedTheRequest: null,
                    type: null,
                    fileIds: null,
                    isDataOverriden: false,
                    dateInitiated: new Date().toISOString(),
                    dateResponseReceived: null,
                    responseOperationId: null,
                    gapSummary: null,
                    queueTasksAhead: null,
                    aiStats: null
                }),
                operationId: operationId,
                requestId: requestId,
                state: DataCollectionAiAgentOperationState.DeleteInProgress,
                agentOperationCode: null,
                agentState: null,
                dateModified: new Date().toISOString(),
                dateCompleted: null
            };

            dispatch({
                type: AISurveyAgentSetState,
                value: {
                    currentOperation: deleteInProgressOperation,
                    isLoading: true,
                    errorMessage: null
                }
            });

            const deleteRequest: AiDeleteAllAnswersStartRequest = {
                requestId: requestId,
                operationId: operationId
            };

            const startResult = await serviceClient.startDeleteAllAiAnswersWorkflow(deleteRequest);

            if (!startResult.success || !startResult.data) {
                dispatch({
                    type: AISurveyAgentSetState,
                    value: {
                        isLoading: false,
                        errorMessage: 'Failed to start delete-all workflow'
                    }
                });

                return {
                    success: false,
                    result: startResult.result,
                    data: null
                };
            }

            const startedDeleteOperation: AiSurveyOperation = {
                ...deleteInProgressOperation,
                operationId: startResult.data,
                dateModified: new Date().toISOString(),
                dateCompleted: null
            };

            dispatch({
                type: AISurveyAgentSetState,
                value: {
                    currentOperation: startedDeleteOperation,
                    isLoading: true,
                    errorMessage: null
                }
            });

            startAIOperationPolling(requestId, startResult.data);

            return {
                success: true,
                result: 'Ok',
                data: startResult.data
            };
        } catch (error) {
            dispatch({
                type: AISurveyAgentSetState,
                value: {
                    isLoading: false,
                    errorMessage: 'An unexpected error occurred while starting delete-all'
                }
            });

            return {
                success: false,
                result: 'Error',
                data: null
            };
        }
    };

/**
 * Terminates an active AI workflow operation.
 * @param requestId The survey request ID
 * @param operationId The AI operation ID
 * @returns Promise with the terminated operation ID
 */
export const terminateAIWorkflow = (
    requestId: string,
    operationId: string
) =>
    async (dispatch: (action: KnownAction) => void, getState: () => ApplicationState): Promise<DataResult<string>> => {
        try {
            if (!requestId || requestId.trim() === '') {
                return {
                    success: false,
                    result: 'Error',
                    data: null
                };
            }

            if (!operationId || operationId.trim() === '') {
                return {
                    success: false,
                    result: 'Error',
                    data: null
                };
            }

            dispatch({
                type: AISurveyAgentSetState,
                value: {
                    isLoading: true,
                    errorMessage: null
                }
            });

            const terminateRequest: AiTerminateRequest = {
                requestId,
                operationId
            };

            const terminateResult = await serviceClient.terminateSupplierAiWorkflow(terminateRequest);

            if (!terminateResult.success || !terminateResult.data) {
                dispatch({
                    type: AISurveyAgentSetState,
                    value: {
                        isLoading: false,
                        errorMessage: 'Failed to terminate AI workflow'
                    }
                });

                return {
                    success: false,
                    result: terminateResult.result,
                    data: null
                };
            }

            stopAIOperationPolling();

            const existingOperation = getActiveAIOperationFromState(getState().aiSurveyAgentState);
            const terminatedAt = new Date().toISOString();
            const terminatedOperation: AiSurveyOperation = {
                ...(existingOperation ?? {
                    ownerId: null,
                    requestId,
                    operationId,
                    userWhoInitiatedTheRequest: null,
                    type: null,
                    fileIds: null,
                    isDataOverriden: false,
                    dateInitiated: terminatedAt,
                    dateResponseReceived: null,
                    responseOperationId: null,
                    gapSummary: null,
                    queueTasksAhead: null
                }),
                requestId,
                operationId,
                state: DataCollectionAiAgentOperationState.TerminatedByUser,
                agentOperationCode: null,
                agentState: null,
                dateModified: terminatedAt,
                dateCompleted: terminatedAt,
                queueTasksAhead: null
            };

            dispatch({
                type: AISurveyAgentSetState,
                value: {
                    currentOperation: terminatedOperation,
                    isLoading: false,
                    isPolling: false,
                    errorMessage: null
                }
            });

            return {
                success: true,
                result: 'Ok',
                data: terminateResult.data
            };
        } catch (error) {
            dispatch({
                type: AISurveyAgentSetState,
                value: {
                    isLoading: false,
                    errorMessage: 'An unexpected error occurred while terminating AI workflow'
                }
            });

            return {
                success: false,
                result: 'Error',
                data: null
            };
        }
    };

/**
 * Polls the AI operation status and updates Redux state
 * @param requestId The survey request ID
 * @param operationId The AI operation ID
 * @returns Promise that polls the operation status
 */
export const pollAIOperationStatus = (
    requestId: string,
    operationId: string
) =>
    async (dispatch: (action: KnownAction) => void, getState: () => ApplicationState): Promise<void> => {
        try {
            // Validate inputs
            if (!requestId || !operationId) {
                return;
            }

            // Call API to get status
            const result = await serviceClient.getAISurveyOperationStatus(requestId, operationId);

            if (result.success && result.data) {
                // Reset retry count on successful poll
                pollingRetryCount = 0;

                // Update state with latest operation data
                dispatch({
                    type: AISurveyAgentSetState,
                    value: {
                        currentOperation: result.data,
                        lastPolledAt: new Date().toISOString(),
                        errorMessage: null
                    }
                });

                // Check if operation is finished
                if (isAIOperationFinished(result.data)) {
                    stopAIOperationPolling();

                    // Set loading to false when finished
                    dispatch({
                        type: AISurveyAgentSetState,
                        value: {
                            isLoading: false
                        }
                    });
                }

            } else {
                // Increment retry count on failure
                pollingRetryCount++;

                // Handle specific error cases
                if (result.result === 'Error' || result.result === 'NotFound') {
                    const currentOperation = getActiveAIOperationFromState(getState().aiSurveyAgentState);

                    if (currentOperation) {
                        dispatch({
                            type: AISurveyAgentSetState,
                            value: {
                                currentOperation: {
                                    ...currentOperation,
                                    state: DataCollectionAiAgentOperationState.Failed
                                },
                                errorMessage: 'AI operation encountered an error'
                            }
                        });
                    }

                    // Stop polling on critical errors
                    stopAIOperationPolling();
                }

                // Stop polling if max retries reached
                if (pollingRetryCount >= MAX_POLLING_RETRIES) {

                    const currentOperation = getActiveAIOperationFromState(getState().aiSurveyAgentState);

                    if (currentOperation) {
                        dispatch({
                            type: AISurveyAgentSetState,
                            value: {
                                currentOperation: {
                                    ...currentOperation,
                                    state: DataCollectionAiAgentOperationState.Failed
                                },
                                errorMessage: 'AI operation polling timed out',
                                isLoading: false
                            }
                        });
                    }

                    stopAIOperationPolling();
                }
            }

        } catch (error) {
            pollingRetryCount++;

            // Stop polling if max retries reached
            if (pollingRetryCount >= MAX_POLLING_RETRIES) {

                const currentOperation = getActiveAIOperationFromState(getState().aiSurveyAgentState);

                if (currentOperation) {
                    dispatch({
                        type: AISurveyAgentSetState,
                        value: {
                            currentOperation: {
                                ...currentOperation,
                                state: DataCollectionAiAgentOperationState.Failed
                            },
                            errorMessage: 'AI operation polling encountered network errors',
                            isLoading: false
                        }
                    });
                }

                stopAIOperationPolling();
            }
        }
    };

/**
 * Starts polling for AI operation status updates
 * @param requestId The survey request ID
 * @param operationId The AI operation ID
 */
export const startAIOperationPolling = (requestId: string, operationId: string): void => {
    // Validate inputs
    if (!requestId || !operationId) {
        return;
    }

    // Stop any existing polling
    if (aiOperationPollingInterval) {
        clearInterval(aiOperationPollingInterval);
    }

    // Reset retry count
    pollingRetryCount = 0;

    // Update state to indicate polling has started
    store.dispatch({
        type: AISurveyAgentSetState,
        value: {
            isPolling: true
        }
    } as KnownAction);

    // Start polling
    const makePollingCall = async () => {
        await store.dispatch(
            pollAIOperationStatus(requestId, operationId) as any
        );
    };

    // Make first call immediately
    makePollingCall();

    // Set up interval for subsequent calls
    aiOperationPollingInterval = setInterval(makePollingCall, POLLING_INTERVAL_MS);
};

/**
 * Stops polling for AI operation status updates
 */
export const stopAIOperationPolling = (): void => {
    if (aiOperationPollingInterval) {
        clearInterval(aiOperationPollingInterval);
        aiOperationPollingInterval = null;
        pollingRetryCount = 0;

        // Update state to indicate polling has stopped
        store.dispatch({
            type: AISurveyAgentSetState,
            value: {
                isPolling: false
            }
        } as KnownAction);
    }
};

/**
 * Clears the current AI operation from state
 * Use this when the user wants to start fresh or clear failed operations
 * @returns Thunk action that clears the AI operation
 */
export const clearAIOperation = (): AppThunkAction<KnownAction> =>
    (dispatch, getState) => {
        // Stop any active polling
        stopAIOperationPolling();

        // Clear state
        dispatch({
            type: AISurveyAgentSetState,
            value: {
                currentOperation: null,
                isLoading: false,
                errorMessage: null,
                lastPolledAt: null
            }
        });
    };

/**
 * Resets the AI Survey Agent state to initial values
 * @returns Thunk action that resets the state
 */
export const resetAISurveyAgentState = (): AppThunkAction<KnownAction> =>
    (dispatch, getState) => {
        // Stop any active polling
        stopAIOperationPolling();

        // Reset to initial state
        dispatch({
            type: AISurveyAgentSetState,
            value: getInitialState()
        });
    };

/**
 * Sets a partial AI Survey Agent state
 * @param partialState Partial state to merge with current state
 * @returns Action to update the state
 */
export const setAISurveyAgentState = (partialState: Partial<AISurveyAgentState>): AppThunkAction<KnownAction> =>
    (dispatch, getState) => {
        dispatch({
            type: AISurveyAgentSetState,
            value: partialState
        });
    };

// ===============================================================================
// Reducer
// ===============================================================================

/**
 * Redux reducer for AI Survey Agent state
 */
export const reducer: Reducer<AISurveyAgentState> = (
    state: AISurveyAgentState | undefined,
    incomingAction: Action
): AISurveyAgentState => {
    if (state === undefined) {
        return getInitialState();
    }

    const action = incomingAction as KnownAction;

    switch (action.type) {
        case AISurveyAgentSetState:
            const newState = {
                ...state,
                ...action.value
            };
            return newState;

        default:
            return state;
    }
};

// ===============================================================================
// Action Creators Export
// ===============================================================================

export const actionCreators = {
    triggerAIWorkflow,
    retriggerAIWorkflow,
    startDeleteAllAiAnswersFlow,
    terminateAIWorkflow,
    pollAIOperationStatus,
    startAIOperationPolling,
    stopAIOperationPolling,
    clearAIOperation,
    resetAISurveyAgentState,
    setAISurveyAgentState
};
